import React, { useState, useContext, useEffect } from 'react';
import { AuthContext, useAPI } from 'components/lib';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import FeatherIcon from 'feather-icons-react';
import { ChartSettingsForm } from '../../work/reports/chartSettingsForm';
import { dataPeriods, defaultCharts } from './data';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import axios from 'axios';
import styles from './Dashboard.module.css';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import "chartjs-plugin-annotation";
import ExploreNav from './nav';


function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function handlePageDragEnd(result, pages, setPages, setCurrentPageIndex) {
  if (!result.destination) return;
  const updatedPages = reorder(pages, result.source.index, result.destination.index);
  setPages(updatedPages);
  setCurrentPageIndex(result.destination.index);
}

export function ReportDashboard({ charts, onSaveChart, showChartSettingsForm, hideAddChartButton }) {
  const authContext = useContext(AuthContext);

  // Initialize state with the charts passed from AiCanvas
  const [pages, setPages] = useState(defaultCharts);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [modalItem, setModalItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [newChartPanelVisible, setNewChartPanelVisible] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("Last 12 Months");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [draggingChartId, setDraggingChartId] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [activeChartMenu, setActiveChartMenu] = useState(null);
  const [hoveredChartId, setHoveredChartId] = useState(null);
  const [hoveredSectionIndex, setHoveredSectionIndex] = useState(null); // Track hovered section index
  const [chartId, setChartId] = useState(null); // Add state for chartId
  const [chartData, setChartData] = useState([]); // State for chart data
  const [initialLoad, setInitialLoad] = useState(true); // Track initial load

  // Fetch data for all charts when the component mounts
  useEffect(() => {
    // if (pages.length === 0 || (pages.length === 1 && pages[0].charts.length === 0)) {
    //   setPages([{ name: 'Page 1', title: 'Page 1', charts: defaultCharts }]);
    // }
  }, []);

  const fetchChartData = async (updatedItem) => {
    const { start_date, end_date } = getPeriodDates(selectedPeriod);
    let url = `/api/appData/reportData/${authContext.user.appConnection}?dataType=${updatedItem.queryOptions.dataType}&dataGroups=${JSON.stringify(updatedItem?.queryOptions?.dataGroups !== undefined ? updatedItem.queryOptions.dataGroups : [])}`;
    if (start_date && end_date) {
      url += `&start_date=${start_date.toISOString()}&end_date=${end_date.toISOString()}`;
    }
    if (updatedItem.queryOptions.dataGroups[0].rowLimit) {
      url += `&rowLimit=${updatedItem.queryOptions.dataGroups[0].rowLimit}`;
    }
    const response = await axios.get(url);
    const data = response.data.data;
    console.log('chartData - ' + JSON.stringify(data));
    return { ...updatedItem, chartData: data };
  };

  useEffect(() => {
    if (!initialLoad) {
      const fetchAllChartData = async (index) => {
        const updatedCharts = await Promise.all(
          pages[index].charts.map(async (item) => {
            const updatedItemWithData = await fetchChartData(item);
            return updatedItemWithData;
          })
        );
        setChartData(prevChartData => {
          const updatedChartData = [...prevChartData];
          updatedChartData[index] = updatedCharts;
          return updatedChartData;
        });
      };
      console.log('currentPageIndex - ' + (currentPageIndex === undefined ? -1 : currentPageIndex));
      var index = currentPageIndex;
      if((currentPageIndex >= pages.length) || (currentPageIndex === undefined)){ 
        index = pages.length - 1;
        setCurrentPageIndex(index);
      }
      fetchAllChartData(index);
      console.log('selectedPeriod - ' + selectedPeriod + ' customStartDate - ' + customStartDate + ' customEndDate - ' + customEndDate + ' currentPageIndex - ' + currentPageIndex); 
    }
  }, [selectedPeriod, customStartDate, customEndDate, currentPageIndex, initialLoad, pages]);

  useEffect(() => {
    if (showChartSettingsForm) {
      setNewChartPanelVisible(true);
    }
  }, [showChartSettingsForm]);

  const chartDataAPI = useAPI('/api/appData/reportDoc/chart');

  useEffect(() => {
    if(chartDataAPI?.data) {
      const charts = chartDataAPI.data[0];
      // console.log('chartData - ' + JSON.stringify(charts));
      if (charts) {
        const parsedData = charts.details;
        setPages(parsedData);
        // Initialize chartData with empty arrays for each page
        setChartData(parsedData.map(() => []));
        // Save the id for future updates
        setChartId(charts.id);
      } else {
        setPages(defaultCharts);
        setChartData(defaultCharts.map(() => []));
      }
      setInitialLoad(false); // Set initial load to false after data is loaded
    }
  }, [chartDataAPI.data]);


  const openNewChartPanel = () => {
    setEditingItem(null);
    setNewChartPanelVisible(true);
  };


  const handleLayoutChange = (newLayout) => {
    const updatedPages = [...pages];
    updatedPages[currentPageIndex] = {
      ...updatedPages[currentPageIndex],
      charts: pages[currentPageIndex].charts.map(item => {
        const layoutItem = newLayout.find(l => l.i === item.i);
        return layoutItem ? { ...item, ...layoutItem } : item;
      })
    };
    setPages(updatedPages);
  };


  const handleEdit = (item, event) => {
    event.stopPropagation(); // Prevent the chart click handler from firing
    setEditingItem(item);
    setNewChartPanelVisible(true);
  };

  const handleDownload = (item) => {
    // Implement the logic to download the chart data to Excel
    console.log(`Downloading chart data for ${item.chartTitle}`);
  };

  const handleDelete = (item) => {
    if (window.confirm(`Are you sure you want to delete the chart?`)) {
      const updatedCharts = pages[currentPageIndex].charts.filter(chart => chart.i !== item.i);
      const updatedPages = [...pages];
      updatedPages[currentPageIndex] = {
        ...updatedPages[currentPageIndex],
        charts: updatedCharts
      };
      setPages(updatedPages);
    }
  };

  const handleDeletePage = (index) => {
    if (window.confirm(`Are you sure you want to delete this section?`)) {
      
      const updatedPages = pages.filter((_, i) => i !== index);
      setPages(updatedPages);
      if (index === currentPageIndex) {
        setCurrentPageIndex(updatedPages.length === 0 ? 0 : Math.min(currentPageIndex, updatedPages.length - 1));
      } else if (index === pages.length - 1) {
        setCurrentPageIndex(0);
      } else {
        setCurrentPageIndex(prevIndex => Math.min(prevIndex, updatedPages.length - 1));
      }
    }
  };

  const getPeriodDates = (period) => {
    const now = new Date();
    let start_date, end_date;

    switch (period) {
      case 'This Month':
        start_date = new Date(now.getFullYear(), now.getMonth(), 1);
        end_date = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'Last Month':
        start_date = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end_date = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'This Quarter':
        const currentQuarter = Math.floor((now.getMonth() + 3) / 3);
        start_date = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
        end_date = new Date(now.getFullYear(), currentQuarter * 3, 0);
        break;
      case 'Last Quarter':
        const lastQuarter = Math.floor((now.getMonth() + 3) / 3) - 1;
        start_date = new Date(now.getFullYear(), (lastQuarter - 1) * 3, 1);
        end_date = new Date(now.getFullYear(), lastQuarter * 3, 0);
        break;
      case 'This Year':
        start_date = new Date(now.getFullYear(), 0, 1);
        end_date = new Date(now.getFullYear(), 11, 31);
        break;
      case 'Last 12 Months':
        start_date = new Date(now.getFullYear(), now.getMonth() - 11, 0);
        // console.log('start_date: ' + start_date)
        end_date = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'Last 3 Years':
        start_date = new Date(now.getFullYear() - 3, 11, 31);
        end_date = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'Last 5 Years':
        start_date = new Date(now.getFullYear() - 5, 11, 31);
        end_date = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      default:
        start_date = customStartDate;
        end_date = customEndDate;
        break;
    }

    return { start_date, end_date };
  };


  useEffect(() => {
    const saveChartData = async () => {
      try {
        console.log('chartId - ' + chartId);
        await axios.post('/api/appData/reportData', {
          id: chartId, // Use the saved id for updates
          report_name: 'chart',
          details: pages
        });
      } catch (error) {
        console.error('Error saving chart settings:', error);
      }
    };

    if (!initialLoad && pages.length > 0) {
      saveChartData();
    }
  }, [pages, chartId, initialLoad]);

  const saveChartSettings = async (updatedItem) => {
    const updatedItemWithData = await fetchChartData(updatedItem);
    const isExistingChart = pages[currentPageIndex].charts.some(item => item.i === updatedItemWithData.i);

    if (isExistingChart) {
      const updatedCharts = pages[currentPageIndex].charts.map(item =>
        item.i === updatedItemWithData.i ? updatedItemWithData : item
      );
      const updatedPages = [...pages];
      updatedPages[currentPageIndex] = {
        ...updatedPages[currentPageIndex],
        charts: updatedCharts
      };
      setPages(updatedPages);
    } else {
      const updatedPages = [...pages];
      updatedPages[currentPageIndex] = {
        ...updatedPages[currentPageIndex],
        charts: [...updatedPages[currentPageIndex].charts, updatedItemWithData]
      };
      setPages(updatedPages);
    }
    setEditingItem(null);
    setNewChartPanelVisible(false);
  };

  const closeModal = () => {
    setModalItem(null);
  };

  const closePanel = () => {
    setEditingItem(null);
    setNewChartPanelVisible(false);
  };


  const addNewPage = () => {
    const newPageIndex = pages.length;
    setPages([...pages, { name: `Page ${newPageIndex + 1}`, title: `Page ${newPageIndex + 1}`, charts: [] }]);
    setCurrentPageIndex(newPageIndex);
  };

  const goToPage = (index) => {
    if (index >= 0 && index < pages.length) {
      setCurrentPageIndex(index);
    }
  };

  const handleDragStart = (chartId) => {
    setDraggingChartId(chartId);
  };

  const handleDragEnd = () => {
    setDraggingChartId(null);
  };

  const handleTitleChange = (e) => {
    const updatedPages = [...pages];
    updatedPages[currentPageIndex].title = e.target.value;
    setPages(updatedPages);
  };

  const toggleEditingTitle = () => {
    setEditingTitle(!editingTitle);
  };

  const getTrimmedTitle = (title) => {
    return title.length > 22 ? `${title.substring(0, 22)}...` : title;
  };

  const toggleChartMenu = (chartId) => {
    setActiveChartMenu(activeChartMenu === chartId ? null : chartId);
  };

  const handleMouseLeave = (chartId) => {
    setHoveredChartId(null);
    if (activeChartMenu === chartId) {
      setActiveChartMenu(null);
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    for (let i = 0; i < pages.length; i++) {
      setCurrentPageIndex(i);
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait for the page to render
      const charts = chartData[i];
      const allDataLoaded = charts.every(chart => chart.chartData);
      if (!allDataLoaded) {
        await fetchAllChartDataForPage(i);
      }
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait for charts to finish rendering
      const canvas = await html2canvas(document.querySelector('.layout'), { scale: 2 });
      const imgData = canvas.toDataURL('image/jpeg', 0.75); // Use JPEG format with 75% quality
      const imgProps = doc.getImageProperties(imgData);
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      doc.text(pages[i].title, 10, 10); // Add the page title at the top
      doc.addImage(imgData, 'JPEG', 0, 20, pdfWidth, pdfHeight - 20); // Adjust the image position
      if (i < pages.length - 1) {
        doc.addPage();
      }
    }
    doc.save('report.pdf');
  };

  const fetchAllChartDataForPage = async (pageIndex) => {
    const updatedCharts = await Promise.all(
      pages[pageIndex].charts.map(async (item) => {
        const updatedItemWithData = await fetchChartData(item);
        return updatedItemWithData;
      })
    );
    const updatedChartData = [...chartData];
    updatedChartData[pageIndex] = updatedCharts;
    setChartData(updatedChartData);
  };

  const renderChart = (item, chartData) => {
    const renderTarget = item.targetValue && (item.chartType === 'bar' || item.chartType === 'line');
    
    // Modern color palettes
    const modernColors = [
      '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
      '#ef4444', '#ec4899', '#84cc16', '#f97316', '#6366f1'
    ];
    
    const gradientColors = [
      'rgba(59, 130, 246, 0.8)', 'rgba(139, 92, 246, 0.8)', 'rgba(6, 182, 212, 0.8)',
      'rgba(16, 185, 129, 0.8)', 'rgba(245, 158, 11, 0.8)', 'rgba(239, 68, 68, 0.8)',
      'rgba(236, 72, 153, 0.8)', 'rgba(132, 204, 22, 0.8)', 'rgba(249, 115, 22, 0.8)',
      'rgba(99, 102, 241, 0.8)'
    ];

    // Enhanced chart data with modern styling
    const enhancedChartData = chartData ? {
      ...chartData,
      datasets: chartData.datasets?.map((dataset, index) => ({
        ...dataset,
        backgroundColor: item.chartType === 'pie' || item.chartType === 'donut' 
          ? modernColors
          : item.chartType === 'bar'
            ? gradientColors[index % gradientColors.length]
            : gradientColors[index % gradientColors.length],
        borderColor: modernColors[index % modernColors.length],
        borderWidth: item.chartType === 'line' ? 3 : 2,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: modernColors[index % modernColors.length],
        pointBorderWidth: 3,
        pointRadius: item.chartType === 'line' ? 6 : 0,
        pointHoverRadius: item.chartType === 'line' ? 8 : 0,
        lineTension: 0.4
      })) || []
    } : { labels: [], datasets: [] };

    const chartProps = {
      data: enhancedChartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: item.queryOptions.showLegend,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 20,
            fontSize: 12,
            fontColor: "#374151"
          }
        },
        tooltips: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleFontColor: "#ffffff",
          bodyFontColor: "#ffffff",
          borderColor: "#3b82f6",
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          mode: "nearest",
          intersect: false
        },
        hover: {
          intersect: false,
          mode: "index"
        },
        scales: item.chartType === 'pie' || item.chartType === 'donut' ? {} : {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              fontColor: "#6b7280",
              fontSize: 11
            }
          }],
          yAxes: [{
            gridLines: {
              color: "rgba(229, 231, 235, 0.5)",
              lineWidth: 1
            },
            ticks: {
              fontColor: "#6b7280",
              fontSize: 11
            }
          }]
        },
        elements: {
          point: {
            radius: 6,
            hoverRadius: 8,
            borderWidth: 3,
            backgroundColor: "#ffffff"
          },
          line: {
            tension: 0.4,
            borderWidth: 3
          },
          rectangle: {
            borderWidth: 2
          }
        },
        animation: {
          duration: 1000,
          easing: "easeInOutQuart"
        },
        ...(renderTarget && {
          annotation: {
            annotations: [{
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: item.targetValue,
              borderColor: '#ef4444',
              borderWidth: 3,
              borderDash: [10, 5],
              label: {
                enabled: true,
                content: `Target: ${item.targetValue}`,
                position: 'right',
                backgroundColor: '#ef4444',
                fontColor: '#ffffff',
                fontStyle: 'bold'
              }
            }]
          }
        })
      }
    };

    switch (item.chartType) {
      case 'bar':
        return <Bar {...chartProps} />;
      case 'line':
        return <Line {...chartProps} />;
      case 'pie':
        return <Pie {...chartProps} />;
      case 'donut':
        return <Doughnut {...chartProps} />;
      default:
        return null;
    }
  };

  return (
    <>
    <ExploreNav />
    {/* Side panel for editing charts */}
    {newChartPanelVisible && (
              <div className={styles.sidePanelRpt} style={{ height: 'calc(100vh - 40px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                <div className={styles.sidePanelContent} style={{ width: '100%', height: '100%', backgroundColor: '#fff', padding: '20px', boxSizing: 'border-box' }}>
                  <ChartSettingsForm item={editingItem || { chartType: 'bar', chartTitle: '', queryOptions: { dataGroups: [{"dataType": 'GL Reports', aggregation: 'SUM', "dataVal":"Balance","groupBy":"Account Type", "filters": []}], showLegend: false }, targetValue: 0, color: '' }} onSave={saveChartSettings} onCancel={closePanel} />
                </div>
              </div>
            )}
            {!newChartPanelVisible && (
      <div style={{ display: 'flex', height: 'calc(100vh - 40px)', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flex: 1 }}>
          <div className={styles.sidebar} style={{ width: '200px', padding: '20px', boxShadow: '2px 0 5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Sections</h3>
              <button onClick={addNewPage} style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                border: 'none', 
                cursor: 'pointer',
                borderRadius: '8px',
                padding: '8px',
                color: 'white',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <FeatherIcon icon="plus" size={16} />
              </button>
            </div>
            <DragDropContext onDragEnd={(result) => handlePageDragEnd(result, pages, setPages, setCurrentPageIndex)}>
              <Droppable droppableId="droppable-pages">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <ul style={{ listStyleType: 'none', padding: 0 }}>
                      {pages.map((p, index) => (
                        <Draggable key={index} draggableId={`page-${index}`} index={index}>
                          {(provided) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: '8px',
                                cursor: 'pointer',
                                paddingLeft: '10px',
                                backgroundColor: currentPageIndex === index ? 'transparent' : 'transparent',
                                background: currentPageIndex === index 
                                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' 
                                  : 'transparent',
                                color: currentPageIndex === index ? 'white' : '#374151',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                height: '40px',
                                borderRadius: '4px',
                                boxShadow: currentPageIndex === index ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                fontWeight: currentPageIndex === index ? '600' : '500',
                                fontSize: '16px'
                              }}
                              onClick={() => goToPage(index)}
                              onMouseEnter={() => setHoveredSectionIndex(index)}
                              onMouseLeave={() => setHoveredSectionIndex(null)}
                            >
                              {getTrimmedTitle(p.title)}
                              {hoveredSectionIndex === index && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); handleDeletePage(index); }} 
                                  style={{ 
                                    background: 'rgba(239, 68, 68, 0.1)', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    padding: '6px', 
                                    color: '#ef4444',
                                    borderRadius: '6px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.2)'}
                                  onMouseLeave={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.1)'}
                                >
                                  <FeatherIcon icon="trash-2" size={14} />
                                </button>
                              )}
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div style={{ 
            flex: 1, 
            padding: '24px', 
            position: 'relative', 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
            borderRadius: '0', 
            overflow: 'auto',
            minHeight: '100vh'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div className={styles.dropdownContainer}>
                <Dropdown value={selectedPeriod} options={dataPeriods.map(period => ({ label: period, value: period }))} onChange={(e) => setSelectedPeriod(e.value)} placeholder="Select Period" />
                {selectedPeriod === 'Custom' && (
                  <>
                    <Calendar value={customStartDate} onChange={(e) => setCustomStartDate(e.value)} view="month" dateFormat="mm/yy" placeholder="Start Date" />
                    <Calendar value={customEndDate} onChange={(e) => setCustomEndDate(e.value)} view="month" dateFormat="mm/yy" placeholder="End Date" />
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <button onClick={openNewChartPanel} className={styles.addChartButton}>Add New Chart</button>
                <button onClick={generatePDF} className={styles.generatePdfButton}>Generate PDF</button>
              </div>
            </div>
            {editingTitle ? (
              <input
                type="text"
                value={pages[currentPageIndex].title}
                onChange={handleTitleChange}
                onBlur={toggleEditingTitle}
                autoFocus
                style={{ 
                  fontSize: '28px', 
                  fontWeight: '700', 
                  marginBottom: '20px', 
                  width: '100%',
                  border: '2px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  color: '#1e293b',
                  outline: 'none',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                }}
              />
            ) : (
              <h2 
                onClick={toggleEditingTitle} 
                style={{ 
                  cursor: 'pointer', 
                  marginBottom: '20px',
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1e293b',
                  letterSpacing: '-0.5px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.target.style.color = '#1e293b'}
              >
                {pages[currentPageIndex].title}
              </h2>
            )}
            <GridLayout
              className={styles.layout}
              layout={pages[currentPageIndex].charts}
              cols={12}
              rowHeight={30}
              width={1200}
              onLayoutChange={handleLayoutChange}
              onDragStart={(layout, oldItem) => handleDragStart(oldItem.i)}
              onDragStop={handleDragEnd}
              minW={5} // Minimum width of 5 cells
              minH={7} // Minimum height of 7 cells
            >
              {pages[currentPageIndex].charts.map((item, index) => (
                <div
                  style={{ zIndex: 100 }}
                  key={item.i}
                  data-grid={item}
                  className={`${styles.chartItem}${draggingChartId === item.i ? ` ${styles.dragging}` : ''}`}
                  onMouseEnter={() => setHoveredChartId(item.i)}
                  onMouseLeave={() => handleMouseLeave(item.i)}
                >
                  <div className={styles.chartHeader} style={{ backgroundColor: item.color}}>
                    <h3>{item.chartTitle}</h3>
                    <div className={styles.chartToolbar}>
                      {hoveredChartId === item.i && (
                        <button onClick={() => toggleChartMenu(item.i)}>
                          <FeatherIcon icon="chevron-down" size={16} color="white" />
                        </button>
                      )}
                      {activeChartMenu === item.i && (
                        <div className={styles.chartMenu}>
                          <button onClick={(event) => handleEdit(item, event)}>
                            <FeatherIcon icon="edit" size={16} /> Edit
                          </button>
                          <button onClick={() => handleDownload(item)}>
                            <FeatherIcon icon="download" size={16} /> Download
                          </button>
                          <button onClick={() => handleDelete(item)}>
                            <FeatherIcon icon="trash-2" size={16} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.chartContainer}>
                    {renderChart(item, chartData[currentPageIndex] && chartData[currentPageIndex][index]?.chartData)}
                  </div>
                </div>
              ))}
            </GridLayout>

            {/* Modal for maximizing charts */}
            {modalItem && (
              <>
                <div className={styles.modalOverlay} onClick={closeModal}></div>
                <div className={styles.rptModal}>
                  <button className={styles.closeButton} onClick={closeModal}>
                    <FeatherIcon icon="x" />
                  </button>
                  <h2>{modalItem.chartTitle}</h2>
                  <div className={styles.chartContainer}>
                    {renderChart(modalItem, modalItem.chartData)}
                  </div>
                </div>
              </>
            )}

            
          </div>
        </div>
      </div>
      )}
    </>
  );
}